import { isEmptyDisplayValue } from '../FieldWrapper/isEmptyDisplayValue';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { getDocumentRequiredMessage } from '@utils/validation/document/requiredMessage';
import { UseFormReturn } from 'react-hook-form';
import clsx from 'clsx';
import { formulaTraceAttrs } from '@/context/DocumentFormulaTraceContext';
import { IDocumentField } from '@models/Forms/IForms';
import FieldWrapper from '../FieldWrapper/FieldWrapper';
import FormFieldView from '../FormFieldView/FormFieldView';
import CheckboxBitControl from '@srm/ui/controls/CheckboxBitControl';
import YesNoControl from '@srm/ui/controls/YesNoControl';
import TextareaControl from '@srm/ui/controls/TextareaControl';
import TextareaBase64Control from '@srm/ui/controls/TextareaBase64Control';
import InputNumberControl from '@srm/ui/controls/InputNumberControl';
import DateTimeFieldControl from '@srm/ui/controls/DateTimeFieldControl';
import YearFieldControl from '@srm/ui/controls/YearFieldControl';
import DateFieldControl from '@srm/ui/controls/DateFieldControl';
import TimeFieldControl from '@srm/ui/controls/TimeFieldControl';
import InputControl from '@srm/ui/controls/InputControl';
import InputPasswordControl from '@srm/ui/controls/InputPasswordControl';
import RatePickerControl from '@srm/ui/controls/RatePickerControl';
import RatePicker from '@srm/ui/rate-picker';
import './Field.scss';
import { IFieldElem } from '@models/IFormData';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FormMessageReserveContext } from '../../FormMessageReserveContext';
import DisplayField from '@molecules/DisplayField/DisplayField';
import { useRegisterFieldChanges, useRunFieldChanges } from '@controls/ChangeHandlers/useChangeHandlers';
import { ValueType } from '@srm/ui/types/ValueType';
import ToolTipTitle from '@atoms/ToolTipTitle/ToolTipTitle';
import { safeFormatDate } from '@utils/date';
import { toDateOrNull } from '@srm/ui/lib/dateFormValue';
import { IPartialFormGroup } from '@srm/ui/controls/types';
import { declensionOfSymbol } from '@utils/helpers';
import { resolveMaxLength } from '@utils/validation/document/evaluateFieldErrors';
import { VALIDATION_MESSAGES } from '@utils/validation/document/messages';
import { useWorkCalendar } from '@hooks/useWorkCalendar';
import {
    useFormulaState,
    useFormulaStateWithStatus,
    useFormulaReplace,
    useFieldSaver,
    useActivated,
    useLateVisibilityCascadeGate,
} from '@molecules/formbuilder/hooks';
import { useDebugPanel } from '@/context/DebugPanelContext';
import FieldFormulaTooltip from '@molecules/DebugPanel/FieldFormulaTooltip/FieldFormulaTooltip';
import { valueTypeToDatatype } from '@molecules/DebugPanel/FieldFormulaTooltip/fieldEditorUtils';
import {
    EnsureExternalDataSourceLoaded,
    extractExternalDataSourceNames,
    parseExternalDataSourceKey,
} from '@/utils/externalDataSourceUtils';

export interface IFieldProps<TFieldValues extends object = object> {
    field?: IDocumentField;
    formMethods: UseFormReturn<TFieldValues>;
    setError: (errors?: string[]) => void;
    isEdit: boolean;
    isNew: boolean;
    turnOffInvokeChangesOnNew: boolean;
    fields: Record<string, IFieldElem>;
    includedFields: number[];
    ensureExternalDataSourceLoaded?: EnsureExternalDataSourceLoaded;
}

const FieldInner = <TFieldValues extends object = object>({
    field,
    formMethods,
    setError,
    isEdit,
    isNew,
    turnOffInvokeChangesOnNew,
    fields,
    includedFields,
    ...props
}: IFieldProps<TFieldValues>) => {
    const { ensureExternalDataSourceLoaded } = props;
    const { activated } = useActivated();
    const { settings: debugSettings, fieldOverrides } = useDebugPanel();
    const reserveMessage = useContext(FormMessageReserveContext);
    const { value: visibilityFromFormula, settled: visibilityFromFormulaSettled } = useFormulaStateWithStatus(
        field?.visibilityRules,
        fields,
        formMethods,
        isEdit,
        isNew,
        activated,
        {
            fallback: true,
            ensureExternalDataSourceLoaded,
            trace: field
                ? {
                      targetId: field.id || field.key,
                      targetKind: 'field',
                      targetName: field.name || field.key,
                      ruleKind: 'visibility',
                  }
                : undefined,
        },
    );
    const readOnlyFromFormula = useFormulaState(field?.readonly, fields, formMethods, isEdit, isNew, activated, {
        fallback: false,
        ensureExternalDataSourceLoaded,
        trace: field
            ? {
                  targetId: field.id || field.key,
                  targetKind: 'field',
                  targetName: field.name || field.key,
                  ruleKind: 'readonly',
              }
            : undefined,
    });
    const { value: maxLengthRulesResult, settled: maxLengthRulesSettled } = useFormulaStateWithStatus(
        field?.maxLengthRules,
        fields,
        formMethods,
        isEdit,
        isNew,
        activated,
        {
            fallback: true,
            ensureExternalDataSourceLoaded,
        },
    );
    // Дебаг-оверрайды поля (жучок): forced-значения правил мимо формул.
    const fieldOverride = field?.key ? fieldOverrides.get(field.key) : undefined;
    const visibility = fieldOverride?.visibility ?? visibilityFromFormula;
    const visibilitySettled = fieldOverride?.visibility !== undefined || visibilityFromFormulaSettled;
    const readOnly = fieldOverride?.readonly ?? readOnlyFromFormula;
    const effPattern = fieldOverride?.pattern ?? field?.pattern;
    const configuredMaxLength = fieldOverride?.maxLength ?? field?.maxLength;
    const maxLengthEnabled = !field?.maxLengthRules || maxLengthRulesResult;
    const maxLengthDisabledByRule = !!field?.maxLengthRules && !maxLengthRulesResult;
    const supportsMaxLength = field?.valueType === ValueType.Text || field?.valueType === ValueType.LongText;
    const effMaxLength =
        supportsMaxLength && maxLengthEnabled && configuredMaxLength != null ? configuredMaxLength : undefined;
    // Лимит длины строки: `max` даёт правило валидации, `maxLength` — ограничение ввода на
    // контроле. Значение из формулы, каскада `changes` или справочника ввод минует, поэтому
    // в правило идёт минимум из обоих — иначе такое значение сохранялось бы без проверки.
    const maxLengthLimit = resolveMaxLength(field?.valueType, field?.max, effMaxLength, maxLengthDisabledByRule);
    const requiredFromFormula = useFormulaState(field?.required, fields, formMethods, isEdit, isNew, activated, {
        fallback: false,
        ensureExternalDataSourceLoaded,
        trace: field
            ? {
                  targetId: field.id || field.key,
                  targetKind: 'field',
                  targetName: field.name || field.key,
                  ruleKind: 'required',
              }
            : undefined,
    });
    const required = fieldOverride?.required ?? requiredFromFormula;
    const workCalendar = useFormulaState(field?.workCalendarRules, fields, formMethods, isEdit, isNew, activated, {
        ensureExternalDataSourceLoaded,
        trace: field
            ? {
                  targetId: field.id || field.key,
                  targetKind: 'field',
                  targetName: field.name || field.key,
                  ruleKind: 'workCalendar',
              }
            : undefined,
    });
    const minValFromFormula = useFormulaReplace(field?.min, fields, formMethods, isEdit, isNew, activated, {
        ensureExternalDataSourceLoaded,
        trace: field
            ? {
                  targetId: field.id || field.key,
                  targetKind: 'field',
                  targetName: field.name || field.key,
                  ruleKind: 'min',
              }
            : undefined,
    });
    const maxValFromFormula = useFormulaReplace(field?.max, fields, formMethods, isEdit, isNew, activated, {
        ensureExternalDataSourceLoaded,
        trace: field
            ? {
                  targetId: field.id || field.key,
                  targetKind: 'field',
                  targetName: field.name || field.key,
                  ruleKind: 'max',
              }
            : undefined,
    });
    const minVal = fieldOverride?.min ?? minValFromFormula;
    const maxVal = fieldOverride?.max ?? maxValFromFormula;
    const onSaveField = useFieldSaver(fields, formMethods);
    const runFieldChanges = useRunFieldChanges();
    const calendar = useWorkCalendar(workCalendar);

    const dataField = fields[field?.key!];
    const idField = dataField?.name;
    const fieldVal = dataField?.value;
    // Несколько адресов (по одному на строку) правятся многострочно: <input> вырезает переводы
    // строк и склеил бы адреса. Режим не сбрасывается, пока поле смонтировано, — иначе удаление
    // последнего перевода строки пересоздало бы редактор под курсором.
    const multiLineUrl = useRef(false);
    if (field?.valueType === ValueType.Url && typeof fieldVal === 'string' && fieldVal.includes('\n')) {
        multiLineUrl.current = true;
    }
    const fieldBinding = useMemo(() => ({ key: field?.key ?? '' }), [field?.key]);
    useRegisterFieldChanges(fieldBinding, field?.changes!, field?.key ?? '', fields, isEdit, isNew, onSaveField, setError, formMethods, undefined, Boolean(field && visibility));

    useEffect(() => {
        if (!field?.maxLengthRules || !maxLengthRulesSettled || !idField) return;
        void formMethods.trigger(idField as any);
    }, [field?.maxLengthRules, formMethods, idField, maxLengthRulesResult, maxLengthRulesSettled]);

    useEffect(() => {
        if (dataField?.index !== undefined && !includedFields.includes(dataField.index)) {
            includedFields.push(dataField.index);
        }
    }, [dataField?.index, includedFields]);

    useEffect(() => {
        if (
            idField != undefined &&
            field?.valueType === ValueType.Boolean &&
            (fieldVal == null || fieldVal === undefined) &&
            (isNew || isEdit)
        ) {
            formMethods.setValue(idField as any, 0 as any, { shouldDirty: true });
        }
    }, [field?.valueType, fieldVal, isNew, isEdit, idField, formMethods]);

    const skipCentralizedCascade = useLateVisibilityCascadeGate(field, isNew, isEdit, visibility, visibilitySettled);
    const runNewInitCascade = useLatest(() => {
        if (skipCentralizedCascade) return;
        if (isNew && field?.invokeChangesOnNew && visibility && !turnOffInvokeChangesOnNew) {
            runFieldChanges(
                field?.changes!,
                fieldVal,
                field?.key!,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                undefined,
                undefined,
                formMethods,
                fieldBinding,
            );
        }
    });
    // Смена callbacks/карты полей не является повторной инициализацией.
    useEffect(() => runNewInitCascade.current(), [isNew, visibility, turnOffInvokeChangesOnNew, skipCentralizedCascade, runNewInitCascade]);

    useEffect(() => {
        if (!idField) return;
        if (debugSettings.skipAllValidation || debugSettings.skipRequiredValidation) {
            formMethods.clearErrors(idField as any);
        }
    }, [debugSettings.skipAllValidation, debugSettings.skipRequiredValidation, idField, formMethods]);

    const runEditInitCascade = useLatest(() => {
        if (skipCentralizedCascade) return;
        if (isEdit && field?.invokeChangesOnEdit && visibility) {
            if (
                idField != undefined &&
                field?.valueType === ValueType.Boolean &&
                fieldVal == null &&
                (isEdit || isNew)
            ) {
                formMethods.setValue(idField as any, 0 as any, { shouldDirty: true });
            }
            runFieldChanges(
                field?.changes!,
                fieldVal,
                field?.key!,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                undefined,
                undefined,
                formMethods,
                fieldBinding,
            );
        }
    });
    // Смена callbacks/карты полей не является повторной инициализацией.
    useEffect(() => runEditInitCascade.current(), [isEdit, visibility, skipCentralizedCascade, runEditInitCascade]);

    const triggerChange = useCallback(
        async (value: any) => {
            await runFieldChanges(
                field?.changes!,
                value,
                field?.key!,
                fields,
                isEdit,
                isNew,
                onSaveField,
                setError,
                undefined,
                undefined,
                formMethods,
                fieldBinding,
            );
        },
        [field?.changes, field?.key, fields, isEdit, isNew, onSaveField, setError, formMethods, runFieldChanges, fieldBinding],
    );

    const getValue = useCallback(
        (value: any) => {
            let fieldValue = value;

            if (field?.replacerFrom && field?.replacerTo && !(fieldValue === undefined || fieldValue === null)) {
                const strValue = fieldValue as string;
                if (strValue) {
                    fieldValue = strValue.replaceAll(field.replacerFrom!, field.replacerTo!);
                }
            }

            if (field?.valueType === ValueType.Url && field?.pattern) {
                if (fieldValue === undefined || fieldValue === null) {
                    return '';
                }
                return field.pattern.replace('{value}', fieldValue as string);
            }
            return fieldValue;
        },
        [field?.replacerFrom, field?.replacerTo, field?.valueType, field?.pattern],
    );

    const onInputChange = useCallback((value: string) => triggerChange(value), [triggerChange]);
    const onInputNumberChange = useCallback((value: number | null) => triggerChange(value), [triggerChange]);
    const onCheckboxChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => triggerChange(event.currentTarget.checked ? 1 : 0),
        [triggerChange],
    );
    const onTextareaChange = useCallback((value: string) => triggerChange(value), [triggerChange]);
    const onDateTimeChange = useCallback((value: Date | null) => triggerChange(value), [triggerChange]);
    const requiredMessage = getDocumentRequiredMessage(field ?? {});
    const checkDate = useCallback(
        (value: any) => {
            try {
                if (debugSettings.skipAllValidation) {
                    return true;
                }
                const skipReq = debugSettings.skipRequiredValidation;
                if (!skipReq && required && (value === undefined || value === null || value === '')) {
                    return requiredMessage;
                }
                // toDateOrNull обрабатывает Date / ISO-string / number / null корректно (без TZ-сдвига
                // для date-only). Старый new Date(value) ломался на ISO "YYYY-MM-DD" — давал local-полночь
                // вместо UTC, что в +TZ окружениях сдвигало день на границе суток.
                // ВАЖНО: клонируем, потому что для Date-инстансов toDateOrNull возвращает тот же объект,
                // а ниже мы мутируем val.setSeconds/setHours — без клона это испортило бы значение в форме.
                const src = toDateOrNull(value);
                if (!src) return true;
                const val = new Date(src.getTime());
                val.setSeconds(0);
                val.setMilliseconds(0);
                if (field?.valueType === ValueType.Date) {
                    val.setHours(0);
                    val.setMinutes(0);
                }

                if (field?.max && value !== undefined && value !== null && value !== '') {
                    const valMax = new Date(+maxVal!);
                    valMax.setSeconds(0);
                    valMax.setMilliseconds(0);
                    if (field?.valueType === ValueType.Date) {
                        valMax.setHours(0);
                        valMax.setMinutes(0);
                    }
                    if (val.getTime() > valMax.getTime()) {
                        const fmt = field?.valueType === ValueType.Date ? 'DD.MM.YYYY' : 'DD.MM.YYYY HH:mm';
                        return 'Максимальное значение: ' + safeFormatDate(valMax, fmt);
                    }
                }
                if (field?.min && value !== undefined && value !== null && value !== '') {
                    const valMin = new Date(+minVal!);
                    valMin.setSeconds(0);
                    valMin.setMilliseconds(0);
                    if (field?.valueType === ValueType.Date) {
                        valMin.setHours(0);
                        valMin.setMinutes(0);
                    }
                    if (val.getTime() < valMin.getTime()) {
                        const fmt = field?.valueType === ValueType.Date ? 'DD.MM.YYYY' : 'DD.MM.YYYY HH:mm';
                        return 'Минимальное значение: ' + safeFormatDate(valMin, fmt);
                    }
                }

                if (workCalendar && calendar.isHoliday(val)) {
                    return 'Выбран выходной день';
                }

                return true;
            } catch (error) {
                console.log(error);
                return true;
            }
        },
        [
            required,
            requiredMessage,
            field?.max,
            field?.min,
            field?.valueType,
            maxVal,
            minVal,
            workCalendar,
            calendar,
            debugSettings.skipAllValidation,
            debugSettings.skipRequiredValidation,
        ],
    );

    const fieldView = (value: any) =>
        !field ? null : (
            <FieldWrapper
                isViewMode={!isEdit && !isNew}
                isEmpty={
                    field.valueType !== ValueType.Boolean &&
                    field.inputType !== 'rating' &&
                    isEmptyDisplayValue(getValue(value))
                }
                inLineFormat={field.isValue}
                label={field.valueType === ValueType.Boolean ? undefined : field.name}
                title={field.viewTitle}
                verticalBottomAlign={field?.verticalBottomAlign}
            >
                {field.inputType === 'password' ? (
                    <span>{value == null || value === '' ? '' : '••••••••'}</span>
                ) : field.inputType === 'rating' && field.valueType === ValueType.Integer ? (
                    <RatePicker
                        aria-label={field.name}
                        data-testid={`rating-${field.key}`}
                        readOnly
                        value={Number(getValue(value)) || 0}
                    />
                ) : (
                    <DisplayField
                        type={field.valueType}
                        floatPoints={field.floatPoints}
                        label={field.name}
                        value={getValue(value)}
                        trueText={field.trueText}
                        falseText={field.falseText}
                        testid={field.key}
                    />
                )}
            </FieldWrapper>
        );

    const fieldControlType = () => {
        if (field && (field.name === undefined || field.name === null)) {
            return <div />;
        }

        const skipReq = debugSettings.skipAllValidation || debugSettings.skipRequiredValidation;
        const skipPat = debugSettings.skipAllValidation || debugSettings.skipPatternValidation;
        const skipLen = debugSettings.skipAllValidation || debugSettings.skipLengthValidation;
        const effectiveRequired = skipReq ? false : required;
        let rules: Record<string, any> = {
            ...{
                required: {
                    value: effectiveRequired ? effectiveRequired : false,
                    message: requiredMessage,
                },
            },
            ...(!skipPat &&
                effPattern &&
                field?.valueType != ValueType.Url && {
                    pattern: {
                        value: new RegExp(effPattern, 'g'),
                        message: 'Неверный формат',
                    },
                }),

            ...(!skipLen &&
                field?.min &&
                (field?.valueType == ValueType.Text ||
                    field?.valueType == ValueType.LongText ||
                    field?.valueType == ValueType.Url) && {
                    minLength: {
                        value: Number(field?.min),
                        message: `Минимальная длина поля: ${field?.min} ${declensionOfSymbol(Number(field?.min))}`,
                    },
                }),
            ...(!skipLen &&
                maxLengthLimit !== undefined && {
                    maxLength: {
                        value: maxLengthLimit,
                        message: VALIDATION_MESSAGES.maxLength(maxLengthLimit),
                    },
                }),
        };
        const stringTypes = [ValueType.Text, ValueType.LongText, ValueType.Url, ValueType.Base64Text];
        if (effectiveRequired && field?.valueType && stringTypes.includes(field.valueType)) {
            rules = {
                ...rules,
                validate: {
                    notWhitespaceOnly: (value: any) => {
                        if (typeof value === 'string' && value.trim() === '') {
                            return requiredMessage;
                        }
                        return true;
                    },
                },
            };
        }
        // Пропсы, одинаковые во всех ветках switch: без них каждая ветка повторяла
        // семь строк, и любую из них легко было забыть в новой.
        const commonProps = {
            label: field?.name,
            tooltip: <ToolTipTitle title={field?.title} />,
            formGroupProps: { verticalBottomAlign: field?.verticalBottomAlign, reserveMessage } as IPartialFormGroup,
            required: required,
            name: idField,
            control: formMethods.control,
        };

        if (field?.displayMode === 'view') {
            const date = [ValueType.Date, ValueType.DateTime, ValueType.NoSecDateTime].includes(field.valueType);
            return (
                <FormFieldView
                    name={idField!}
                    control={formMethods.control}
                    rules={date ? { validate: { checkDate } } : rules}
                >
                    {fieldView}
                </FormFieldView>
            );
        }

        switch (field?.valueType) {
            case ValueType.Boolean:
                return (
                    <CheckboxBitControl
                        {...commonProps}
                        readOnly={readOnly}
                        disabled={readOnly}
                        rules={rules}
                        onChange={onCheckboxChange}
                        testid={field?.key}
                    />
                );
            case ValueType.YesNo:
                return (
                    <YesNoControl
                        {...commonProps}
                        trueText={field?.trueText}
                        falseText={field?.falseText}
                        legacy={field?.legacy}
                        disabled={readOnly}
                        options={[]}
                        rules={rules}
                        onValueChange={onInputNumberChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.LongText:
                return (
                    <TextareaControl
                        {...commonProps}
                        readOnly={readOnly}
                        autoResize={field?.autoResize}
                        rules={rules}
                        onValueChange={onTextareaChange}
                        maxLength={effMaxLength}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        aiAutocompleteKey={field?.aiAutocompleteKey}
                        testid={field?.key}
                    />
                );
            case ValueType.Base64Text:
                return (
                    <TextareaBase64Control
                        {...commonProps}
                        readOnly={readOnly}
                        autoResize={field?.autoResize}
                        rules={rules}
                        onValueChange={onTextareaChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.Double:
                return (
                    <InputNumberControl
                        {...commonProps}
                        groupBy={0}
                        separator={''}
                        readOnly={readOnly}
                        max={field?.max ? Number(maxVal!) : undefined}
                        min={field?.min ? Number(minVal!) : undefined}
                        floatPoints={field?.floatPoints ?? 2}
                        mask={field?.mask}
                        rules={rules}
                        onValueChange={onInputNumberChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.Money:
                return (
                    <InputNumberControl
                        {...commonProps}
                        readOnly={readOnly}
                        max={field?.max ? Number(maxVal!) : undefined}
                        min={field?.min ? Number(minVal!) : undefined}
                        floatPoints={field?.floatPoints ?? 2}
                        rules={rules}
                        mask={field?.mask}
                        onValueChange={onInputNumberChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.Integer:
                if (field?.inputType === 'rating') {
                    return (
                        <RatePickerControl
                            aria-label={field?.name}
                            {...commonProps}
                            data-testid={`rating-${field?.key}`}
                            onValueChange={onInputNumberChange}
                            readOnly={readOnly}
                            rules={rules}
                        />
                    );
                }

                return (
                    <InputNumberControl
                        {...commonProps}
                        groupBy={0}
                        separator={''}
                        readOnly={readOnly}
                        max={field?.max ? Number(maxVal!) : undefined}
                        min={field?.min ? Number(minVal!) : undefined}
                        rules={rules}
                        floatPoints={0}
                        //type="number"
                        pattern={'^[\\d]+$'}
                        mask={field?.mask}
                        onValueChange={onInputNumberChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.NoSecDateTime:
                return (
                    <DateTimeFieldControl
                        {...commonProps}
                        readOnly={readOnly}
                        defaultValue={fieldVal as any}
                        rules={{
                            validate: { checkDate },
                        }}
                        maxDate={field?.max ? (toDateOrNull(maxVal) ?? undefined) : undefined}
                        minDate={field?.min ? (toDateOrNull(minVal) ?? undefined) : undefined}
                        workCalendar={workCalendar}
                        withSeconds={false}
                        onValueChange={onDateTimeChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.Year:
                return (
                    <YearFieldControl
                        {...commonProps}
                        disabled={readOnly}
                        max={field?.max ? Number(maxVal!) : undefined}
                        min={field?.min ? Number(minVal!) : undefined}
                        defaultValue={fieldVal as any}
                        rules={rules}
                        onValueChange={(v) => v != null && onInputNumberChange(v)}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.DateTime:
                return (
                    <DateTimeFieldControl
                        {...commonProps}
                        readOnly={readOnly}
                        defaultValue={fieldVal as any}
                        rules={{
                            validate: { checkDate },
                        }}
                        maxDate={field?.max ? (toDateOrNull(maxVal) ?? undefined) : undefined}
                        minDate={field?.min ? (toDateOrNull(minVal) ?? undefined) : undefined}
                        withSeconds={true}
                        onValueChange={onDateTimeChange}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.Date:
                return (
                    <DateFieldControl
                        {...commonProps}
                        readOnly={readOnly}
                        defaultValue={fieldVal as any}
                        rules={{
                            validate: { checkDate },
                        }}
                        maxDate={field?.max ? (toDateOrNull(maxVal) ?? undefined) : undefined}
                        minDate={field?.min ? (toDateOrNull(minVal) ?? undefined) : undefined}
                        workCalendar={workCalendar}
                        onValueChange={onDateTimeChange}
                        isValueAsString={field?.isValueAsString}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.Time:
                return (
                    <TimeFieldControl
                        {...commonProps}
                        readOnly={readOnly}
                        defaultValue={fieldVal as any}
                        rules={rules}
                        onValueChange={onInputChange}
                        testid={field?.key}
                    />
                );
            case ValueType.Text:
                if (field?.inputType === 'password') {
                    return (
                        <InputPasswordControl
                            {...commonProps}
                            readOnly={readOnly}
                            rules={rules}
                            onValueChange={onInputChange}
                            maxLength={effMaxLength}
                            placeholder={field?.placeholder ? field.placeholder : undefined}
                            autoComplete="new-password"
                            // Селектор легаси: data-testid="input-<key>" (соседние ветки Field
                            // дают тот же через deprecated-проп testid фасада Input).
                            data-testid={`input-${field?.key}`}
                        />
                    );
                }

                return (
                    <InputControl
                        {...commonProps}
                        readOnly={readOnly}
                        rules={rules}
                        mask={field?.mask}
                        onValueChange={onInputChange}
                        pattern={field?.inputRegExp}
                        maxLength={effMaxLength}
                        showMaxLengthCounter={effMaxLength != null}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );
            case ValueType.Url:
                if (multiLineUrl.current) {
                    return (
                        <TextareaControl
                            {...commonProps}
                            readOnly={readOnly}
                            autoResize
                            rules={rules}
                            onValueChange={onTextareaChange}
                            placeholder={field?.placeholder ? field.placeholder : undefined}
                            testid={field?.key}
                        />
                    );
                }
                return (
                    <InputControl
                        {...commonProps}
                        readOnly={readOnly}
                        rules={rules}
                        mask={field?.mask}
                        onValueChange={onInputChange}
                        pattern={field?.inputRegExp}
                        placeholder={field?.placeholder ? field.placeholder : undefined}
                        testid={field?.key}
                    />
                );

            default:
                return (
                    <div>
                        {field?.valueType} --- {fieldVal && fieldVal.toString()} --- {typeof fieldVal}
                    </div>
                );
        }
    };

    return field && field.key && visibility && idField != undefined ? (
        <div
            className={clsx(
                'form-field',
                (isEdit || isNew) && field.displayMode !== 'view' && readOnly && 'form-field--readonly',
            )}
            data-testid={field.id ? `field-${field.id}` : undefined}
            data-document-field-index={dataField.index}
            {...formulaTraceAttrs(field.id || field.key, 'field')}
        >
            {debugSettings.showFieldFormulas && (
                <FieldFormulaTooltip
                    required={field.required}
                    visibilityRules={field.visibilityRules}
                    readonly={field.readonly}
                    min={field.min}
                    max={field.max}
                    pattern={field.pattern}
                    maxLength={field.maxLength}
                    workCalendarRules={field.workCalendarRules}
                    fieldKey={field.key}
                    isViewMode={!isEdit && !isNew}
                    rhfPath={idField}
                    datatype={valueTypeToDatatype(field.valueType)}
                    value={fieldVal}
                    requiredResult={requiredFromFormula}
                    readonlyResult={readOnlyFromFormula}
                    visibilityResult={visibilityFromFormula}
                    minResult={minValFromFormula}
                    maxResult={maxValFromFormula}
                />
            )}
            {isEdit || isNew ? fieldControlType() : fieldView(fieldVal)}
        </div>
    ) : (
        <></>
    );
};

const Field = <TFieldValues extends object = object>(props: IFieldProps<TFieldValues>) => {
    const { field, isEdit, isNew, ensureExternalDataSourceLoaded } = props;

    const externalSourceNames = useMemo(
        () =>
            !isNew
                ? extractExternalDataSourceNames(
                      !isEdit ? field?.key : undefined,
                      field?.visibilityRules,
                      field?.required,
                      field?.readonly,
                      field?.min,
                      field?.max,
                      field?.maxLengthRules,
                  )
                : [],
        [
            field?.key,
            field?.visibilityRules,
            field?.required,
            field?.readonly,
            field?.min,
            field?.max,
            field?.maxLengthRules,
            isEdit,
            isNew,
        ],
    );
    const isExternalField = useMemo(() => !!parseExternalDataSourceKey(field?.key), [field?.key]);
    const [externalDataSourceReady, setExternalDataSourceReady] = useState(externalSourceNames.length === 0);

    useEffect(() => {
        if (externalSourceNames.length === 0 || !ensureExternalDataSourceLoaded) {
            setExternalDataSourceReady(true);
            return;
        }

        let canceled = false;
        setExternalDataSourceReady(false);
        Promise.all(externalSourceNames.map((name) => ensureExternalDataSourceLoaded(name))).finally(() => {
            if (!canceled) {
                setExternalDataSourceReady(true);
            }
        });

        return () => {
            canceled = true;
        };
    }, [externalSourceNames, ensureExternalDataSourceLoaded]);

    if (isExternalField && (isEdit || isNew)) return <></>;
    if (!externalDataSourceReady) return <></>;

    return <FieldInner {...props} />;
};

export default Field;
